import { useState, useEffect, useRef } from 'react'

import type { FirstDataRenderedEvent, RowClickedEvent, RowSelectedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IAppManagementByDeviceType } from '~/types/application-management'
import type { MobileDeviceType } from '~/types/device-type'

import columns from './columns'

import { ModalBody } from '~/components/Modal/Layout'
import TableWrapper from '~/components/Tables/TableWrapper'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { splitByDeviceType } from '~/utils/application'

import './styles.scss'

interface Props {
  onAppSelectorRowClicked: (data: { app_key: string; device_type: MobileDeviceType }) => void
  selectedApp: {
    id?: string
    name?: string
  }
}

const AppSelector = ({ onAppSelectorRowClicked, selectedApp }: Props): ReactElement => {
  /** Local state */

  const gridRef = useRef<AgGridReact<IAppSummary>>(null)

  const [selectedAppId, setSelectedAppId] = useState<string | undefined>(selectedApp.id)

  /** Queries & Mutations */

  const { data: apps } = useAppsManagementListQuery()

  const sortedData = apps
    ?.reduce(splitByDeviceType({ withWeb: false }), [] as IAppManagementByDeviceType[])
    .sort(({ app_key: aName }, { app_key: bName }) => aName.localeCompare(bName))

  /** Effects */

  useEffect(() => {
    setSelectedAppId(selectedApp.id)
  }, [selectedApp.id])

  /** Handlers */

  const handleFirstDataRendered = (evt: FirstDataRenderedEvent) => {
    // NOTE: do not use destructuring on the node object otherwise ag-grid will lose
    // the this reference because of the arrow function
    selectedAppId && evt.api.forEachNode((node) => node.data?.app_id === selectedAppId && node.setSelected(true))
  }

  const handleRowSelected = (evt: RowSelectedEvent) => {
    selectedAppId &&
      // NOTE: do not use destructuring on the node object otherwise ag-grid will lose
      // the this reference because of the arrow function
      gridRef.current?.api.forEachNode(
        (node) => node.isSelected() && node.rowIndex !== null && evt.api.ensureIndexVisible(node.rowIndex, 'top')
      )
  }

  const handleRowClicked = (evt: RowClickedEvent<IAppManagementByDeviceType>) =>
    evt.data && onAppSelectorRowClicked(evt.data as { app_key: string; device_type: MobileDeviceType })

  /** Render */

  return (
    <ModalBody className="app-selector-modal-container">
      <div className="app-selector-modal-content">
        <TableWrapper
          columnDefs={columns}
          entriesName="apps"
          includeHiddenColumnsInQuickFilter
          onFirstDataRendered={handleFirstDataRendered}
          onRowClicked={handleRowClicked}
          onRowSelected={handleRowSelected}
          ref={gridRef}
          rowData={sortedData}
          withPagination={false}
          withSearchBar
          withSearchBarAutoFocus
        />
      </div>
    </ModalBody>
  )
}

export default AppSelector
